import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { clearAuth } from '@/lib/auth'
import { Calendar, Users, Package, Settings, LogOut, Bell } from 'lucide-react'

export function Layout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  // OPT-042：未读通知徽标（30s 轮询，未读 0 时不显示）
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.getNotificationUnread(),
    refetchInterval: 30_000,
  })
  const unread = unreadData?.count ?? 0

  // OPT-051：邮箱未验证横幅（宽松模式，不阻断功能；复用 ['me'] 缓存）
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me() })
  const showVerifyBanner = me?.emailVerified === false
  const [resent, setResent] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  // 冷却倒计时（每秒递减）
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const handleResendVerification = async () => {
    try {
      await api.resendVerification()
      setResent(true)
      setCooldown(60)
    } catch (e) {
      // 429 限频：透传服务端剩余秒数进入冷却
      const secs = (e as { retryAfterSeconds?: number }).retryAfterSeconds
      if (typeof secs === 'number') setCooldown(secs)
    }
  }

  // OPT-031：管理后台挂 /app 前缀；OPT-042：新增消息通知项
  const navItems = [
    { path: '/app', label: t('nav.schedule'), icon: Calendar },
    { path: '/app/clients', label: t('nav.clients'), icon: Users },
    { path: '/app/package-templates', label: t('nav.packageTemplates'), icon: Package },
    { path: '/app/notifications', label: t('nav.notifications'), icon: Bell },
    { path: '/app/settings', label: t('nav.settings'), icon: Settings },
  ]

  /** 未读徽标（仅在消息项且未读 > 0 时渲染） */
  const UnreadBadge = ({ path }: { path: string }) =>
    path === '/app/notifications' && unread > 0 ? (
      <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
        {unread > 99 ? '99+' : unread}
      </span>
    ) : null

  const handleLogout = async () => {
    try { await api.logout() } catch {}
    clearAuth()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-white">
      {/* 桌面端：左侧竖排导航（参考 Loticnature PT 侧边栏） */}
      <aside
        data-testid="side-nav"
        className="hidden md:flex fixed inset-y-0 left-0 z-10 w-56 flex-col bg-white border-r border-slate-200"
      >
        <div className="h-16 shrink-0 flex items-center px-5 border-b border-slate-200">
          <Link to="/" className="text-xl font-bold text-slate-900">{t('app.name')}</Link>
        </div>
        <nav className="flex-1 flex flex-col gap-1 p-3 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
                <UnreadBadge path={item.path} />
              </Link>
            )
          })}
        </nav>
        <div className="shrink-0 flex items-center justify-end p-3 border-t border-slate-200">
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </aside>

      {/* 移动端：保留顶部横排导航 */}
      <header className="md:hidden bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-slate-900">{t('app.name')}</Link>
              <nav className="flex gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon
                  const active = location.pathname === item.path
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      aria-label={item.label}
                      className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm font-medium transition-colors ${
                        active
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      <span className="relative">
                        <Icon className="h-4 w-4" />
                        {item.path === '/app/notifications' && unread > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold">
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </span>
                    </Link>
                  )
                })}
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* 主内容：桌面端左移出侧边栏宽度，全宽铺满（大屏不留白） */}
      <main className="md:pl-56">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          {/* OPT-051：邮箱未验证横幅（宽松模式提示，可重发，60s 冷却） */}
          {showVerifyBanner && (
            <div
              data-testid="verify-email-banner"
              className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800"
            >
              <span>
                {t('verifyEmail.banner.message')}
                {resent && <span className="ml-2 text-amber-600">{t('verifyEmail.banner.resent')}</span>}
              </span>
              <Button
                data-testid="verify-email-resend"
                variant="outline"
                size="sm"
                disabled={cooldown > 0}
                onClick={handleResendVerification}
              >
                {cooldown > 0
                  ? t('verifyEmail.banner.cooldown', { seconds: cooldown })
                  : t('verifyEmail.banner.resend')}
              </Button>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  )
}
